import { Buyer } from '@40seas/entities/buyers';
import { Checkout } from '@40seas/entities/checkout';
import {
    EntityReferenceWithAmount,
    EntityReferenceWithAmountSchema,
    EnumSchema,
    Executor,
    FromSchema,
    ZodDate,
} from '@40seas/entities/common';
import { Invoice } from '@40seas/entities/invoices';
import { PaymentMethod } from '@40seas/entities/payment-methods';
import { Seller } from '@40seas/entities/sellers';
import { BaseEntity, Currency, Fee, FeeStatus, WithBaseEntitySchema } from '@40seas/models';
import { z } from 'zod';
import { ChargeType } from './charge-type.enum';
import { ChargeFailureReason, ChargeFailureReasonSchema } from './charge.failure.reason.enum';
import { ChargeStatus } from './charge.status.enum';
import { ProviderFee, ProviderFeeSchema } from './provider-fee.entity';

export const ChargeSchema = WithBaseEntitySchema(
    z.object({
        buyer: z.string().or(Buyer['schema']),
        amount: z.number().max(1000000000),
        paymentMethod: z.string().or(PaymentMethod['schema']),
        paymentMethodOptions: z.record(z.string(), z.any()).optional(),
        providerId: z.string().optional(),
        currency: EnumSchema(Currency),
        status: EnumSchema(ChargeStatus),
        type: EnumSchema(ChargeType),
        invoice: z
            .string()
            .or(z.lazy(() => Invoice['schema']))
            .optional(),
        invoices: z.array(EntityReferenceWithAmountSchema).optional(),
        availableOn: ZodDate.optional(),
        amountCaptured: z.number().optional(),
        fee: Fee['schema'].optional(),
        feeStatus: EnumSchema(FeeStatus).optional(),
        checkout: z.string().or(Checkout['schema']).optional(),
        failureCode: z.string().optional(),
        failureReason: ChargeFailureReasonSchema.optional(),
        failureMessage: z.string().optional(),
        note: z.string().max(250).optional(),
        chargeDate: ZodDate,
        providerFees: z.array(ProviderFeeSchema).optional(),
    }),
)
    .strict()
    .openapi({
        title: 'Charge',
    });

export type PaymentMethodOptions = {
    [key: string]: any;
};

export class Charge extends FromSchema(ChargeSchema) implements BaseEntity {
    id: string;
    buyer: string | Buyer;
    amount: number;
    paymentMethod: string | PaymentMethod;
    paymentMethodOptions?: PaymentMethodOptions;
    providerId?: string;
    currency: Currency;
    status: ChargeStatus;
    type: ChargeType;
    invoice?: string | Invoice;
    invoices?: EntityReferenceWithAmount[];
    availableOn?: Date;
    amountCaptured?: number;
    fee?: Fee;
    feeStatus?: FeeStatus;
    checkout?: string | Checkout;
    chargeDate: Date;
    createdAt: Date;
    updatedAt: Date;
    seller: string | Seller;
    createdBy?: Executor;
    tenantId: string;
    failureCode?: string;
    failureReason?: ChargeFailureReason;
    failureMessage?: string;
    note?: string;
    providerFees?: ProviderFee[];
    metadata?: { [key: string]: string };
    timeline?: { [status: string]: { updatedAt: Date } };
}

export class MultiTenantCharge extends Charge {}
